import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/lib/format";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";

const SHAKTI_BG =
  "https://static.prod-images.emergentagent.com/jobs/a38e0bc3-e5e1-4946-a4e2-33aa200f541b/images/0b58cdf3e407c2daf00e0d78ca68039fce6786f2b2f0fd70f1bb9cfad2707114.png";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("demo@mirrorloops.in");
  const [password, setPassword] = useState("Demo@123");
  const [busy, setBusy] = useState(false);

  const routeFor = (role) => {
    if (role === "hq_admin") return "/app/hq";
    if (role === "franchise_owner") return "/app/franchise";
    if (role === "manager" || role === "staff") return "/app/admin";
    return "/app/dashboard";
  };

  const doLogin = async (e, creds) => {
    const em = creds?.email ?? email;
    const pw = creds?.password ?? password;
    if (creds) {
      setEmail(em);
      setPassword(pw);
    }
    setBusy(true);
    try {
      const u = await login(em, pw);
      toast.success(`Welcome, ${u.name}`);
      nav(routeFor(u.role));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const submit = (e) => {
    e.preventDefault();
    doLogin();
  };

  const quickLogin = (creds) => doLogin(null, creds);

  return (
    <div className="min-h-screen bg-[#0A0908] relative flex items-center justify-center p-6">
      <img
        src={SHAKTI_BG}
        alt=""
        className="absolute inset-0 w-full h-full object-cover opacity-[0.08]"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0A0908] to-[#0A0908]" />
      <Link
        to="/"
        className="absolute top-6 left-6 flex items-center gap-2 text-zinc-400 hover:text-white text-sm"
        data-testid="login-back-link"
      >
        <ArrowLeft size={16} /> Back
      </Link>

      <div className="relative w-full max-w-md rounded-3xl border border-white/10 bg-white/[0.02] backdrop-blur-2xl p-10">
        <div className="flex items-center gap-3 mb-8">
          <BrandLogo size={40} />
          <span className="font-bold tracking-tight text-lg">Mirrorloops</span>
        </div>
        <h1 className="text-3xl font-black tracking-tight mb-2">Welcome back.</h1>
        <p className="text-zinc-400 text-sm mb-8">
          Sign in to book, manage memberships, or run your hub.
        </p>

        <form onSubmit={submit} className="space-y-5">
          <div>
            <Label htmlFor="email" className="text-xs uppercase tracking-[0.2em] text-zinc-500">
              Email
            </Label>
            <Input
              id="email"
              type="email"
              data-testid="login-email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="mt-2 bg-white/5 border-white/10 text-white"
            />
          </div>
          <div>
            <Label htmlFor="pw" className="text-xs uppercase tracking-[0.2em] text-zinc-500">
              Password
            </Label>
            <Input
              id="pw"
              type="password"
              data-testid="login-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="mt-2 bg-white/5 border-white/10 text-white"
            />
          </div>
          <Button
            type="submit"
            disabled={busy}
            data-testid="login-submit-btn"
            className="w-full bg-[#D4B878] hover:bg-[#E8D6A3] rounded-full py-6 font-bold"
          >
            {busy ? "Signing in…" : "Sign In"}
          </Button>
        </form>

        <div className="mt-6 text-sm text-zinc-400 text-center">
          New here?{" "}
          <Link to="/signup" className="text-[#E8D6A3] font-semibold" data-testid="login-register-link">
            Create account
          </Link>
        </div>

        <div className="mt-8 pt-6 border-t border-white/5">
          <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 mb-3">
            One-click demo
          </div>
          <div className="grid grid-cols-2 gap-2" data-testid="login-demo-accounts">
            {[
              { label: "Customer", email: "demo@mirrorloops.in", password: "Demo@123", tone: "text-[#E8D6A3]" },
              { label: "Manager", email: "staff@mirrorloops.in", password: "Staff@123", tone: "text-[#CCFF00]" },
              { label: "Franchise", email: "franchise@mirrorloops.in", password: "Franchise@123", tone: "text-[#00E5FF]" },
              { label: "HQ Admin", email: "hq@mirrorloops.in", password: "Admin@123", tone: "text-white" },
            ].map((d) => (
              <button
                key={d.email}
                type="button"
                disabled={busy}
                onClick={() => quickLogin(d)}
                data-testid={`login-demo-${d.label.toLowerCase().replace(/\s/g, "-")}`}
                className="text-left p-3 border border-white/10 hover:border-white/30 hover:bg-white/[0.03] transition disabled:opacity-50"
              >
                <div className={`text-xs font-bold ${d.tone}`}>{d.label}</div>
                <div className="text-[10px] text-zinc-500 truncate font-mono mt-0.5">
                  {d.email}
                </div>
              </button>
            ))}
          </div>
          <div className="text-[10px] text-zinc-600 mt-3 text-center">
            Click any role to sign in instantly.
          </div>
        </div>
      </div>
    </div>
  );
}
